// ============================================================
// MAP VIEW COMPONENT - Interactive Google Maps
// File: src/components/MapView.js
// Shows all establishments on map with color-coded pins
// ============================================================

import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { GoogleMap, Marker, InfoWindow, useJsApiLoader } from '@react-google-maps/api'
import toast from 'react-hot-toast'
import { useTheme } from '../ThemeContext'

// Note: For client-side React apps, the API key must be in an environment variable
// prefixed with REACT_APP_ to be accessible via process.env
import { MAPS_LIBRARIES, MAPS_API_KEY } from '../mapsConfig'

// Map container style
const mapContainerStyle = {
  width: '100%',
  height: '600px',
  borderRadius: '8px'
}

// Default center (Raleigh, NC area)
const defaultCenter = {
  lat: 35.7796,
  lng: -78.6382
}

// Strict coordinate validation — falsy check alone misses NaN, Infinity, out-of-range
const hasValidCoords = (est) =>
  typeof est.latitude === 'number' && isFinite(est.latitude) &&
  est.latitude >= -90 && est.latitude <= 90 &&
  typeof est.longitude === 'number' && isFinite(est.longitude) &&
  est.longitude >= -180 && est.longitude <= 180

// Libraries must match AdminDashboard to avoid loading conflicts

export default function MapView({ establishments, onSelectEstablishment, establishmentType = 'mxb' }) {
  const { colors } = useTheme()
  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: MAPS_API_KEY,
    libraries: MAPS_LIBRARIES
  })
  
  const [map, setMap] = useState(null)
  const [selectedMarker, setSelectedMarker] = useState(null)
  const [userLocation, setUserLocation] = useState(null)
  const [showingUserLocation, setShowingUserLocation] = useState(false)

  // Get status and color for each establishment
  const getEstablishmentStatus = useCallback((establishment) => {
    // For beer/wine establishments, always use neutral gray (no status tracking)
    if (establishmentType === 'beer_wine') {
      return { status: 'neutral', color: '#6b7280' } // Gray - no status tracking
    }

    if (!establishment.inspections || establishment.inspections.length === 0) {
      return { status: 'never', color: '#9ca3af' } // Gray
    }

    const lastInspection = new Date(
      Math.max(...establishment.inspections.map(i => new Date(i.inspection_date)))
    )

    const today = new Date()
    const daysSince = Math.floor((today - lastInspection) / (1000 * 60 * 60 * 24))

    if (daysSince > 365) return { status: 'overdue', color: '#dc2626' } // Red
    if (daysSince > 180) return { status: 'due_soon', color: '#f59e0b' } // Yellow/Orange
    return { status: 'current', color: '#059669' } // Green
  }, [establishmentType])

  // Create custom marker icon based on status
  const createMarkerIcon = (color) => {
    // Use simple colored markers with proper Google Maps types for cross-browser compatibility
    const icon = {
      url: `data:image/svg+xml,${encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32">
          <path d="M16 2C10.5 2 6 6.5 6 12c0 7 10 18 10 18s10-11 10-18c0-5.5-4.5-10-10-10z"
                fill="${color}"
                stroke="white"
                stroke-width="2"/>
          <circle cx="16" cy="12" r="4" fill="white"/>
        </svg>
      `)}`
    }

    // Use proper Google Maps types when available (fixes Windows/cross-browser issues)
    if (window.google?.maps?.Size) {
      icon.scaledSize = new window.google.maps.Size(32, 32)
      icon.anchor = new window.google.maps.Point(16, 32)
    } else {
      icon.scaledSize = { width: 32, height: 32 }
      icon.anchor = { x: 16, y: 32 }
    }

    return icon
  }

  // User location marker (blue dot)
  const createUserLocationIcon = () => {
    const icon = {
      url: `data:image/svg+xml,${encodeURIComponent(`
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24">
          <circle cx="12" cy="12" r="10" fill="#2563eb" stroke="white" stroke-width="3"/>
        </svg>
      `)}`
    }

    // Use proper Google Maps types when available
    if (window.google?.maps?.Size) {
      icon.scaledSize = new window.google.maps.Size(24, 24)
      icon.anchor = new window.google.maps.Point(12, 12)
    } else {
      icon.scaledSize = { width: 24, height: 24 }
      icon.anchor = { x: 12, y: 12 }
    }

    return icon
  }

  // Get user's current location
  const showMyLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const location = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          }
          setUserLocation(location)
          setShowingUserLocation(true)
          
          // Center map on user location
          if (map) {
            map.panTo(location)
            map.setZoom(12)
          }
        },
        (error) => {
          console.error('Error getting location:', error)
          toast.error('Unable to get your location. Please enable location services.')
        }
      )
    } else {
      toast.error('Geolocation is not supported by your browser.')
    }
  }

  // Fit map to show all markers
  const fitBounds = useCallback(() => {
    if (!map || !window.google?.maps) return
    try {
      const bounds = new window.google.maps.LatLngBounds()
      let pointCount = 0
      establishments.forEach(est => {
        if (hasValidCoords(est)) {
          bounds.extend({ lat: est.latitude, lng: est.longitude })
          pointCount++
        }
      })
      if (userLocation) {
        bounds.extend(userLocation)
        pointCount++
      }
      if (pointCount > 0) map.fitBounds(bounds)
    } catch (err) {
      console.error('MapView fitBounds error:', err)
    }
  }, [map, establishments, userLocation])

  // Fit bounds when establishments change
  useEffect(() => {
    if (map && establishments.length > 0) {
      // Small delay to ensure map is ready
      setTimeout(fitBounds, 100)
    }
  }, [establishments, fitBounds, map])

  const onLoad = useCallback((map) => {
    setMap(map)
    setTimeout(() => {
      if (!window.google?.maps || establishments.length === 0) return
      try {
        const bounds = new window.google.maps.LatLngBounds()
        let pointCount = 0
        establishments.forEach(est => {
          if (hasValidCoords(est)) {
            bounds.extend({ lat: est.latitude, lng: est.longitude })
            pointCount++
          }
        })
        if (pointCount > 0) map.fitBounds(bounds)
      } catch (err) {
        console.error('MapView onLoad bounds error:', err)
      }
    }, 500)
  }, [establishments])

  const onUnmount = useCallback(() => {
    setMap(null)
  }, [])

  // Get label for status
  const getStatusLabel = (status) => {
    const labels = {
      current: 'Current',
      due_soon: 'Due Soon',
      overdue: 'Overdue',
      never: 'Never Inspected'
    }
    return labels[status] || 'Unknown'
  }

  // Format last inspection date
  const getLastInspectionText = (establishment) => {
    if (!establishment.inspections || establishment.inspections.length === 0) {
      return 'Never inspected'
    }
    const lastDateStr = establishment.inspections
      .map(i => i.inspection_date)
      .sort()
      .at(-1)
    const [y, m, d] = lastDateStr.split('-')
    return new Date(Number(y), Number(m) - 1, Number(d)).toLocaleDateString()
  }

  // Memoize so options object reference is stable — avoids Google Maps re-applying controls on every render
  const mapOptions = useMemo(() => ({
    streetViewControl: false,
    mapTypeControl: true,
    fullscreenControl: true,
    fullscreenControlOptions: window.google?.maps?.ControlPosition
      ? { position: window.google.maps.ControlPosition.TOP_RIGHT }
      : undefined,
    zoomControl: true
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [isLoaded]) // re-evaluate once when Maps API finishes loading

  // Handle loading and errors
  if (loadError) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#dc2626' }}>
        <h3>Error loading Google Maps</h3>
        <p>{loadError.message}</p>
        <p style={{ fontSize: '0.875rem', color: colors.text.secondary, marginTop: '1rem' }}>
          Please check your internet connection and API key configuration.
        </p>
      </div>
    )
  }

  if (!isLoaded) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <div style={{ fontSize: '1.25rem', color: colors.text.secondary }}>
          Loading map...
        </div>
      </div>
    )
  }

  return (
    <div style={{ position: 'relative' }}>
      <style>
        {`
          @media (max-width: 768px) {
            .map-legend {
              display: none !important;
            }
            .map-fit-bounds-btn {
              display: none !important;
            }
            .map-location-btn {
              width: 40px !important;
              height: 40px !important;
              padding: 0 !important;
              display: flex !important;
              align-items: center !important;
              justify-content: center !important;
            }
            .map-location-btn-text {
              display: none !important;
            }
            .map-controls {
              bottom: 20px !important;
              top: auto !important;
              left: 20px !important;
            }
          }
        `}
      </style>

      {/* Control Buttons */}
      <div className="map-controls" style={{
        position: 'absolute',
        top: '10px',
        left: '10px',
        zIndex: 10,
        display: 'flex',
        gap: '0.5rem'
      }}>
        <button
          className="map-location-btn"
          onClick={showMyLocation}
          style={{
            padding: '0.75rem 1rem',
            backgroundColor: '#2563eb',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: '500',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
            display: 'flex',
            alignItems: 'center',
            gap: '0.5rem'
          }}
          title="Show My Location"
        >
          {/* Location crosshair icon */}
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="8"/>
            <line x1="12" y1="2" x2="12" y2="8"/>
            <line x1="12" y1="16" x2="12" y2="22"/>
            <line x1="2" y1="12" x2="8" y2="12"/>
            <line x1="16" y1="12" x2="22" y2="12"/>
          </svg>
          <span className="map-location-btn-text">Show My Location</span>
        </button>

        <button
          className="map-fit-bounds-btn"
          onClick={fitBounds}
          style={{
            padding: '0.75rem 1rem',
            backgroundColor: colors.bg.primary,
            color: colors.text.primary,
            border: `1px solid ${colors.border.primary}`,
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '0.875rem',
            fontWeight: '500',
            boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
          }}
        >
          Fit All Pins
        </button>
      </div>

      {/* Legend - Only show for MXB establishments */}
      {establishmentType === 'mxb' && (
        <div className="map-legend" style={{
          position: 'absolute',
          bottom: '20px',
          left: '20px',
          zIndex: 10,
          backgroundColor: colors.bg.primary,
          padding: '1rem',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          fontSize: '0.875rem',
          border: `1px solid ${colors.border.primary}`
        }}>
          <div style={{ fontWeight: '600', marginBottom: '0.5rem', color: colors.text.primary }}>Status Legend</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: '#059669', borderRadius: '50%' }}></div>
              <span style={{ color: colors.text.primary }}>Current (within 6 months)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: '#f59e0b', borderRadius: '50%' }}></div>
              <span style={{ color: colors.text.primary }}>Due Soon (6-12 months)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: '#dc2626', borderRadius: '50%' }}></div>
              <span style={{ color: colors.text.primary }}>Overdue (&gt;12 months)</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <div style={{ width: '12px', height: '12px', backgroundColor: '#9ca3af', borderRadius: '50%' }}></div>
              <span style={{ color: colors.text.primary }}>Never Inspected</span>
            </div>
            {showingUserLocation && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.25rem', paddingTop: '0.25rem', borderTop: `1px solid ${colors.border.primary}` }}>
                <div style={{ width: '12px', height: '12px', backgroundColor: '#2563eb', borderRadius: '50%' }}></div>
                <span style={{ color: colors.text.primary }}>Your Location</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Google Map */}
      <GoogleMap
        mapContainerStyle={mapContainerStyle}
        center={defaultCenter}
        zoom={10}
        onLoad={onLoad}
        onUnmount={onUnmount}
        options={mapOptions}
      >
          {/* Establishment Markers */}
          {establishments.map((est) => {
            if (!hasValidCoords(est)) return null
            
            const { color } = getEstablishmentStatus(est)
            
            return (
              <Marker
                key={est.id}
                position={{ lat: est.latitude, lng: est.longitude }}
                icon={createMarkerIcon(color)}
                onClick={() => setSelectedMarker(est)}
                title={est.trade_name}
              />
            )
          })}

          {/* User Location Marker */}
          {userLocation && (
            <Marker
              position={userLocation}
              icon={createUserLocationIcon()}
              title="Your Location"
            />
          )}

          {/* Info Window */}
          {selectedMarker && (
            <InfoWindow
              position={{ lat: selectedMarker.latitude, lng: selectedMarker.longitude }}
              onCloseClick={() => setSelectedMarker(null)}
            >
              <div style={{ padding: '0.5rem', maxWidth: '250px' }}>
                <h3 style={{ margin: '0 0 0.5rem 0', fontSize: '1rem', fontWeight: '600' }}>
                  {selectedMarker.trade_name}
                </h3>
                <div style={{ fontSize: '0.875rem', color: '#6b7280' }}>
                  <p style={{ margin: '0.25rem 0' }}>
                    <strong>{establishmentType === 'mxb' ? 'MXB' : 'File #'}:</strong> {selectedMarker.mxb_number}
                  </p>
                  <p style={{ margin: '0.25rem 0' }}>
                    <strong>Address:</strong> {selectedMarker.address}
                  </p>
                  <p style={{ margin: '0.25rem 0' }}>
                    <strong>City:</strong> {selectedMarker.city}
                  </p>
                  {establishmentType === 'mxb' && (
                    <p style={{ margin: '0.25rem 0' }}>
                      <strong>Status:</strong>{' '}
                      <span style={{ color: getEstablishmentStatus(selectedMarker).color, fontWeight: '600' }}>
                        {getStatusLabel(getEstablishmentStatus(selectedMarker).status)}
                      </span>
                    </p>
                  )}
                  <p style={{ margin: '0.25rem 0' }}>
                    <strong>Last Inspection:</strong> {getLastInspectionText(selectedMarker)}
                  </p>
                  {selectedMarker.users && (
                    <p style={{ margin: '0.25rem 0' }}>
                      <strong>Agent:</strong> {selectedMarker.users.first_name} {selectedMarker.users.last_name}
                    </p>
                  )}
                </div>
                <div style={{ marginTop: '0.75rem', display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => {
                      setSelectedMarker(null)
                      onSelectEstablishment(selectedMarker)
                    }}
                    style={{
                      padding: '0.5rem 0.75rem',
                      backgroundColor: '#2563eb',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontWeight: '500'
                    }}
                  >
                    View Details
                  </button>
                  <a
                    href={`https://www.google.com/maps/dir/?api=1&destination=${selectedMarker.latitude},${selectedMarker.longitude}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      padding: '0.5rem 0.75rem',
                      backgroundColor: '#059669',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.75rem',
                      fontWeight: '500',
                      textDecoration: 'none',
                      display: 'inline-block'
                    }}
                  >
                    Directions
                  </a>
                </div>
              </div>
            </InfoWindow>
          )}
      </GoogleMap>
    </div>
  )
}
